import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  TrendingUp, TrendingDown, Users, IndianRupee, Target, BarChart3,
  AlertTriangle, Lightbulb, Clock, ArrowUpRight, Activity
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } });

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await axios.get(`${API}/admin/analytics`, authHeader());
        setData(res.data);
      } catch (err) {
        console.error("Failed to fetch analytics:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-3 border-gray-200 border-t-black rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white border border-gray-200 p-12 text-center">
        <AlertTriangle size={40} className="text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 font-dm-sans">Failed to load analytics</p>
      </div>
    );
  }

  const { summary, top_trainings, revenue_trend, status_breakdown, recent_activity, attention_needed } = data;

  // Find max revenue for chart scaling
  const maxRevenue = Math.max(...revenue_trend.map(r => r.revenue), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-500 text-sm font-dm-sans mt-1">Track your training performance and revenue</p>
        </div>
        <div className="text-xs text-gray-400 font-dm-sans">
          Last updated: {new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total Revenue */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-6 shadow-lg shadow-emerald-200"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <IndianRupee size={24} />
            </div>
            {summary.revenue_trend_pct !== 0 && (
              <div className={`flex items-center gap-1 text-sm font-bold ${summary.revenue_trend_pct > 0 ? 'text-emerald-100' : 'text-red-200'}`}>
                {summary.revenue_trend_pct > 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                {Math.abs(summary.revenue_trend_pct)}%
              </div>
            )}
          </div>
          <div className="text-3xl font-outfit font-black mb-1">
            ₹{summary.total_revenue.toLocaleString('en-IN')}
          </div>
          <div className="text-emerald-100 text-sm font-dm-sans">Total Revenue</div>
          <div className="text-emerald-200 text-xs font-dm-sans mt-2">
            ₹{summary.revenue_this_month.toLocaleString('en-IN')} this month
          </div>
        </motion.div>

        {/* Total Registrations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 shadow-lg shadow-blue-200"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <Users size={24} />
            </div>
            {summary.registrations_trend_pct !== 0 && (
              <div className={`flex items-center gap-1 text-sm font-bold ${summary.registrations_trend_pct > 0 ? 'text-blue-100' : 'text-red-200'}`}>
                {summary.registrations_trend_pct > 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                {Math.abs(summary.registrations_trend_pct)}%
              </div>
            )}
          </div>
          <div className="text-3xl font-outfit font-black mb-1">
            {summary.total_registrations}
          </div>
          <div className="text-blue-100 text-sm font-dm-sans">Total Registrations</div>
          <div className="text-blue-200 text-xs font-dm-sans mt-2">
            {summary.registrations_this_month} this month
          </div>
        </motion.div>

        {/* Fill Rate */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-violet-500 to-violet-600 text-white p-6 shadow-lg shadow-violet-200"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <Target size={24} />
            </div>
          </div>
          <div className="text-3xl font-outfit font-black mb-1">
            {summary.fill_rate}%
          </div>
          <div className="text-violet-100 text-sm font-dm-sans">Average Fill Rate</div>
          <div className="w-full h-2 bg-white/20 rounded-full mt-3">
            <div 
              className="h-full bg-white rounded-full transition-all duration-500"
              style={{ width: `${Math.min(summary.fill_rate, 100)}%` }}
            />
          </div>
        </motion.div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trend Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-outfit font-bold text-gray-900 text-lg">Revenue Trend</h2>
              <p className="text-gray-500 text-xs font-dm-sans">Last 6 months</p>
            </div>
            <BarChart3 size={20} className="text-gray-400" />
          </div>
          
          {/* Bar Chart */}
          <div className="flex items-end justify-between gap-3 h-48">
            {revenue_trend.map((item, i) => (
              <div key={i} className="flex-1 flex flex-col items-center">
                <div className="text-xs text-gray-500 font-dm-sans mb-2">
                  ₹{(item.revenue / 1000).toFixed(1)}k
                </div>
                <div 
                  className="w-full bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t transition-all duration-700 hover:from-emerald-600 hover:to-emerald-500"
                  style={{ 
                    height: `${Math.max((item.revenue / maxRevenue) * 100, 5)}%`,
                    minHeight: '8px'
                  }}
                />
                <div className="text-xs text-gray-600 font-dm-sans font-medium mt-2">
                  {item.month}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Top Performing Trainings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-outfit font-bold text-gray-900 text-lg">Top Performing</h2>
              <p className="text-gray-500 text-xs font-dm-sans">By revenue</p>
            </div>
            <TrendingUp size={20} className="text-emerald-500" />
          </div>
          
          {top_trainings.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm font-dm-sans">
              No revenue data yet
            </div>
          ) : (
            <div className="space-y-4">
              {top_trainings.map((training, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm ${
                    i === 0 ? 'bg-amber-100 text-amber-700' :
                    i === 1 ? 'bg-gray-100 text-gray-600' :
                    i === 2 ? 'bg-orange-100 text-orange-700' :
                    'bg-gray-50 text-gray-500'
                  }`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate font-dm-sans">{training.title}</div>
                    <div className="text-xs text-gray-500 font-dm-sans">{training.registrations} registrations</div>
                  </div>
                  <div className="text-sm font-bold text-emerald-600 font-dm-sans">
                    ₹{training.revenue.toLocaleString('en-IN')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Training Status Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white border border-gray-200 p-6"
        >
          <h2 className="font-outfit font-bold text-gray-900 text-lg mb-4">Training Status</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <span className="text-sm text-gray-600 font-dm-sans">Active</span>
              </div>
              <span className="text-sm font-bold text-gray-900">{status_breakdown.active}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-sm text-gray-600 font-dm-sans">Upcoming</span>
              </div>
              <span className="text-sm font-bold text-gray-900">{status_breakdown.upcoming}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gray-400" />
                <span className="text-sm text-gray-600 font-dm-sans">Completed</span>
              </div>
              <span className="text-sm font-bold text-gray-900">{status_breakdown.completed}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-400" />
                <span className="text-sm text-gray-600 font-dm-sans">Draft</span>
              </div>
              <span className="text-sm font-bold text-gray-900">{status_breakdown.draft}</span>
            </div>
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-outfit font-bold text-gray-900 text-lg">Recent Activity</h2>
            <Activity size={18} className="text-gray-400" />
          </div>
          {recent_activity.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm font-dm-sans">
              No recent registrations
            </div>
          ) : (
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {recent_activity.slice(0, 5).map((activity, i) => (
                <div key={i} className="flex items-start gap-3 pb-3 border-b border-gray-100 last:border-0">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Users size={14} className="text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate font-dm-sans">{activity.name}</div>
                    <div className="text-xs text-gray-500 truncate font-dm-sans">{activity.training_title}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Recommendations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 p-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb size={20} className="text-amber-600" />
            <h2 className="font-outfit font-bold text-gray-900 text-lg">Recommendations</h2>
          </div>
          {attention_needed.length === 0 ? (
            <div className="flex items-center gap-3 p-4 bg-white/60 rounded-lg">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                <TrendingUp size={20} className="text-emerald-600" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900 font-dm-sans">All trainings performing well!</div>
                <div className="text-xs text-gray-500 font-dm-sans">No immediate actions needed</div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {attention_needed.map((item, i) => (
                <div key={i} className="p-3 bg-white/60 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-gray-900 font-dm-sans">{item.title}</div>
                      <div className="text-xs text-amber-700 font-dm-sans">{item.issue}</div>
                      <div className="text-xs text-gray-600 font-dm-sans mt-1 flex items-center gap-1">
                        <ArrowUpRight size={10} />
                        {item.suggestion}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
